import { gregorianToEthiopic } from "ethiopian-calendar-finova";
import {
  FileText,
  Gavel,
  History,
  Landmark,
  Repeat,
  Search,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchSuspectHistory } from "services/lealIntityService";
import { recordRoute } from "utils/recordRoutes";

/**
 * A suspect's history from registration up to cassation: the phases reached,
 * the current status and every recorded decision about the person, newest
 * first (GET /legal-entity/suspects/{id}/history/).
 */
type HistoryEvent = {
  phase: Phase;
  kind: string;
  status?: string | null;
  decision?: string | null;
  detail?: string | null;
  reference?: string | null;
  date?: string | null;
  by?: "prosecutor" | "defendant";
  defendant_number?: number;
  /** The record page this entry opens. */
  target?: { model: string; id: string } | null;
};

type Phase = "investigation" | "charge" | "court" | "appeal" | "cassation";

type SuspectHistory = {
  name: string;
  reference?: string | null;
  phases: Phase[];
  reached: Phase[];
  current?: { phase: Phase; status: string } | null;
  events: HistoryEvent[];
};

type Lang = "en" | "am" | "or";

const TEXT: Record<Lang, Record<string, string>> = {
  en: {
    heading: "Suspect history",
    current: "Current status",
    steps: "steps",
    latest: "Latest",
    empty: "No decisions recorded yet.",
    failed: "The history could not be loaded.",
    investigation: "Investigation",
    charge: "Charge",
    court: "Court",
    appeal: "Appeal",
    cassation: "Cassation",
    registered: "Registered as a suspect",
    closed_art_42: "Closed — not prosecuted (Art. 42)",
    witness_conversion: "Conversion to witness",
    bail: "Bail right",
    charged: "Charged",
    defendant: "Defendant",
    pre_judgement: "Court ruling before judgement",
    verdict: "Court verdict",
    appeal_filed_prosecutor: "Appeal filed by the prosecutor",
    appeal_filed_defendant: "Appeal filed by the defendant",
    appeal_decided: "Court decision on the appeal",
    execution: "Execution of the judgement",
    decision: "Decision",
    open: "Open details",
  },
  am: {
    heading: "የተጠርጣሪው ታሪክ",
    current: "አሁን ያለበት ሁኔታ",
    steps: "ደረጃዎች",
    latest: "የቅርብ",
    empty: "እስካሁን የተመዘገበ ውሳኔ የለም።",
    failed: "ታሪኩን መጫን አልተቻለም።",
    investigation: "ምርመራ",
    charge: "ክስ",
    court: "ፍርድ ቤት",
    appeal: "ይግባኝ",
    cassation: "ሰበር",
    registered: "በተጠርጣሪነት ተመዝግቧል",
    closed_art_42: "ተዘግቷል — ክስ አልተመሰረተም (አንቀጽ 42)",
    witness_conversion: "ወደ ምስክርነት መቀየር",
    bail: "የዋስትና መብት",
    charged: "ክስ ተመስርቷል",
    defendant: "ተከሳሽ",
    pre_judgement: "ከፍርድ በፊት የፍርድ ቤት ውሳኔ",
    verdict: "የፍርድ ቤት ፍርድ",
    appeal_filed_prosecutor: "ዐቃቤ ሕግ ይግባኝ አቅርቧል",
    appeal_filed_defendant: "ተከሳሹ ይግባኝ አቅርቧል",
    appeal_decided: "በይግባኙ ላይ የፍርድ ቤት ውሳኔ",
    execution: "የፍርድ አፈጻጸም",
    decision: "ውሳኔ",
    open: "ዝርዝሩን ክፈት",
  },
  or: {
    heading: "Seenaa shakkamaa",
    current: "Haala amma",
    steps: "sadarkaalee",
    latest: "Haaraa",
    empty: "Hanga ammaatti murtiin galmaa'e hin jiru.",
    failed: "Seenaa fe'uun hin danda'amne.",
    investigation: "Qorannoo",
    charge: "Himannaa",
    court: "Mana murtii",
    appeal: "Ol'iyyannoo",
    cassation: "Dhaddacha ijibbaataa",
    registered: "Akka shakkamaatti galmaa'e",
    closed_art_42: "Cufame — hin himatamne (Kwt. 42)",
    witness_conversion: "Gara ragaatti jijjiiramuu",
    bail: "Mirga wabii",
    charged: "Himatame",
    defendant: "Himatamaa",
    pre_judgement: "Murtii mana murtii murtii dura",
    verdict: "Murtii mana murtii",
    appeal_filed_prosecutor: "Abbaan alangaa ol'iyyate",
    appeal_filed_defendant: "Himatamaan ol'iyyate",
    appeal_decided: "Murtii ol'iyyannoo irratti",
    execution: "Raawwii murtii",
    decision: "Murtii",
    open: "Bal'ina bani",
  },
};

const MONTHS: Record<Lang, string[]> = {
  en: ["Meskerem", "Tikimt", "Hidar", "Tahsas", "Tir", "Yekatit", "Megabit", "Miyazya", "Ginbot", "Sene", "Hamle", "Nehase", "Pagume"],
  am: ["መስከረም", "ጥቅምት", "ኅዳር", "ታኅሣሥ", "ጥር", "የካቲት", "መጋቢት", "ሚያዝያ", "ግንቦት", "ሰኔ", "ሐምሌ", "ነሐሴ", "ጳጉሜ"],
  or: ["Fulbaana", "Onkoloolessa", "Sadaasa", "Muddee", "Amajjii", "Guraandhala", "Bitootessa", "Eebila", "Caamsaa", "Waxabajjii", "Adoolessa", "Hagayya", "Qaammee"],
};

const PHASE_ICON: Record<Phase, React.ElementType> = {
  investigation: Search,
  charge: FileText,
  court: Gavel,
  appeal: Repeat,
  cassation: Landmark,
};

const humanize = (code: string) =>
  code.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const SuspectHistoryModal: React.FC<{
  suspectId: string;
  suspectName?: string;
  onClose: () => void;
}> = ({ suspectId, suspectName, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (["en", "am", "or"].includes(i18n.language?.slice(0, 2))
    ? i18n.language.slice(0, 2)
    : "en") as Lang;
  const text = TEXT[lang];
  const [history, setHistory] = useState<SuspectHistory | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let current = true;
    fetchSuspectHistory(suspectId)
      .then((data) => current && setHistory(data))
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [suspectId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const status = (code?: string | null) =>
    code ? t(code, { defaultValue: humanize(code) }) : "";

  const dateEC = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const e = gregorianToEthiopic(d);
    return `${e.day} ${MONTHS[lang][e.month - 1] || e.month} ${e.year} EC`;
  };

  // Open the entry's record (charge, appeal, bail...) and close the panel.
  const openRecord = (e: HistoryEvent) => {
    const route = recordRoute(e.target?.model, e.target?.id);
    if (!route) return;
    onClose();
    navigate(route[0], route[1] ? { state: route[1] } : undefined);
  };

  const title = (e: HistoryEvent) =>
    e.kind === "appeal_filed"
      ? text[`appeal_filed_${e.by || "prosecutor"}`]
      : text[e.kind] || humanize(e.kind);

  const phases = history?.phases || ["investigation", "charge", "court", "appeal", "cassation"];
  const reached = new Set(history?.reached || []);
  const lastReached = Math.max(-1, ...phases.map((p, i) => (reached.has(p) ? i : -1)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={text.heading}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-100 px-5 pb-4 pt-5 sm:px-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FDEBEC] text-[#AA232F]">
              <History size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {text.heading}
              </p>
              <p className="truncate text-lg font-semibold text-gray-900">
                {history?.name || suspectName}
                {history?.reference && (
                  <span className="ml-2 text-sm font-semibold text-[#AA232F]">
                    {history.reference}
                  </span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
            >
              <X size={16} />
            </button>
          </div>

          {history?.current && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
              <p className="flex items-center gap-2 text-gray-600">
                {text.current}
                <span className="rounded-full bg-[#FDEBEC] px-3 py-1 text-xs font-semibold text-[#AA232F]">
                  {text[history.current.phase]} · {status(history.current.status)}
                </span>
              </p>
              <p className="text-xs text-gray-500">
                {history.events.length} {text.steps}
              </p>
            </div>
          )}

          {/* Phases */}
          <ol className="mt-4 flex items-start">
            {phases.map((phase, i) => {
              const Icon = PHASE_ICON[phase];
              const done = reached.has(phase);
              const isCurrent = history?.current?.phase === phase;
              return (
                <li key={phase} className="flex flex-1 flex-col items-center">
                  <div className="flex w-full items-center">
                    <span
                      className={`h-0.5 flex-1 ${i === 0 ? "invisible" : i <= lastReached ? "bg-[#AA232F]/60" : "bg-gray-200"}`}
                    />
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${
                        isCurrent
                          ? "border-[#AA232F] bg-[#FDEBEC] text-[#AA232F] ring-4 ring-[#AA232F]/10"
                          : done
                            ? "border-[#AA232F]/60 bg-[#FDEBEC] text-[#AA232F]"
                            : "border-gray-200 bg-white text-gray-300"
                      }`}
                    >
                      <Icon size={15} />
                    </span>
                    <span
                      className={`h-0.5 flex-1 ${i === phases.length - 1 ? "invisible" : i < lastReached ? "bg-[#AA232F]/60" : "bg-gray-200"}`}
                    />
                  </div>
                  <span
                    className={`mt-1.5 text-center text-[11px] font-semibold ${done ? "text-gray-900" : "text-gray-400"}`}
                  >
                    {text[phase]}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {failed ? (
            <p className="py-10 text-center text-sm text-gray-500">{text.failed}</p>
          ) : !history ? (
            <div className="flex justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#AA232F]" />
            </div>
          ) : history.events.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">{text.empty}</p>
          ) : (
            <ol className="relative">
              {history.events.map((e, i) => {
                const Icon = PHASE_ICON[e.phase] || History;
                const last = i === history.events.length - 1;
                return (
                  <li key={i} className="relative flex gap-3 pb-4">
                    {!last && (
                      <span className="absolute left-[17px] top-9 h-[calc(100%-1.75rem)] w-px bg-gray-200" />
                    )}
                    <span className="z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#AA232F]/40 bg-[#FDEBEC] text-[#AA232F]">
                      <Icon size={15} />
                    </span>
                    <div
                      className={`min-w-0 flex-1 rounded-xl border p-3 ${
                        i === 0 ? "border-[#AA232F]/40 bg-[#FDEBEC]/40" : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-[#AA232F]/30 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#AA232F]">
                          {text[e.phase]}
                        </span>
                        {e.date && (
                          <span className="text-xs text-gray-500">{dateEC(e.date)}</span>
                        )}
                        {i === 0 && (
                          <span className="ml-auto rounded-md bg-gray-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                            {text.latest}
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 font-semibold text-gray-900">
                        {title(e)}
                        {e.kind === "charged" && e.defendant_number
                          ? ` — ${text.defendant} ${e.defendant_number}`
                          : ""}
                      </p>
                      {(e.status || e.decision) && (
                        <p className="text-sm text-gray-700">
                          {status(e.status)}
                          {e.decision && e.decision !== "Pending"
                            ? ` · ${text.decision}: ${status(e.decision)}`
                            : ""}
                        </p>
                      )}
                      {e.detail && (
                        <p className="mt-0.5 line-clamp-2 break-words text-sm text-gray-600" title={e.detail}>
                          {e.detail}
                        </p>
                      )}
                      {(e.reference || recordRoute(e.target?.model, e.target?.id)) &&
                        (recordRoute(e.target?.model, e.target?.id) ? (
                          <button
                            type="button"
                            onClick={() => openRecord(e)}
                            className="mt-1 text-xs font-semibold text-[#AA232F] hover:underline"
                            title={text.open}
                          >
                            → {e.reference || text.open}
                          </button>
                        ) : (
                          <p className="mt-1 text-xs font-semibold text-[#AA232F]">→ {e.reference}</p>
                        ))}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuspectHistoryModal;
